// What was actually done on a programme day, for the next week's session
// (#92 phase 2). The generator was told to "add reps, then 2.5 kg" but only
// ever saw exercise names, so progression was guesswork. This reads the saved
// session back: each main lift's sets, reps and load, how many sets were
// done, and the session's effort rating.
//
// The saved plan is the record of loads: a weight changed mid-session is
// written back into the plan tree (useWorkoutCoach.updateActiveLoad). Reps
// are the prescribed reps of each set done, unless the user corrected them
// during the rest after the set (#97, `actualReps` on the phase-log entry).

import { isRepUnit } from "./planNodeUtils";
import { MIN_SET_SECONDS } from "./phaseLog";

export { MIN_SET_SECONDS };
const MAX_LIFTS = 6;

const groupUnit = (node) => (node?.type === "Group" ? String(node.unit || "").toLowerCase() : "");
// A group whose value is a count of sets: straight sets and supersets
// ("sets"), and circuits, where each round is a set of every move ("rounds").
const isSetCountGroup = (node) => ["sets", "rounds"].includes(groupUnit(node));

// Work nodes inside a set-count group, through any plain wrapper groups
// (a superset pair), but not into another set-count group.
const worksIn = (group) => {
  const found = [];
  const walk = (node) => {
    for (const child of node.children || []) {
      if (child?.type === "Work") found.push(child);
      else if (child?.type === "Group" && !isSetCountGroup(child)) walk(child);
    }
  };
  walk(group);
  return found;
};

const hasLoad = (work) =>
  Number(work.weight) > 0 ||
  Number(work.intensityPct) > 0 ||
  Array.isArray(work.setScheme?.weight) ||
  Array.isArray(work.setScheme?.intensityPct);

const perSet = (work, key, sets) => {
  const scheme = work.setScheme?.[key];
  if (Array.isArray(scheme) && scheme.length) return scheme.slice(0, sets);
  const value = Number(work[key]);
  return Number.isFinite(value) && value > 0 ? Array(sets).fill(value) : null;
};

const sameAll = (list) => list.every((v) => v === list[0]);
const join = (list) => (sameAll(list) ? String(list[0]) : list.join("/"));

/**
 * The main lifts in a saved plan, in order: reps-counted Work nodes in a
 * "sets" group (straight sets, supersets), and loaded ones in a "rounds"
 * circuit (bodyweight circuit moves aren't lifts to progress). Loads come
 * from `weight` (kg) or `intensityPct` (% 1RM), per set when a setScheme
 * varies them.
 */
export const extractLifts = (planData) => {
  const lifts = [];
  const walk = (node) => {
    if (!node || typeof node !== "object") return;
    if (isSetCountGroup(node)) {
      const sets = Math.max(1, Math.round(Number(node.value) || 1));
      const circuit = groupUnit(node) === "rounds";
      for (const work of worksIn(node)) {
        if (!isRepUnit(work.unit) || (circuit && !hasLoad(work))) continue;
        const weight = perSet(work, "weight", sets);
        lifts.push({
          id: work.id,
          label: work.label || node.label || "Lift",
          sets,
          reps: perSet(work, "value", sets),
          weight,
          // Only one load dimension takes effect; kg wins when both are set.
          intensityPct: weight ? null : perSet(work, "intensityPct", sets),
        });
      }
      return; // a group's own children are its sets, not more lifts
    }
    (node.children || []).forEach(walk);
  };
  walk(planData);
  return lifts;
};

/**
 * Reps done on each set, per Work node id, in order, from the session's phase
 * log: the corrected `actualReps` when the user changed it (#97), otherwise
 * the prescribed reps. Null without a log.
 */
export const performedSetsById = (planData) => {
  const log = planData?.phase_log;
  if (!Array.isArray(log)) return null;
  const done = {};
  for (const entry of log) {
    if (!entry?.nodeId) continue;
    if (Number(entry.actualDurationSec) >= MIN_SET_SECONDS && entry.skipReason !== "sessionEnded") {
      const reps = entry.actualReps ?? entry.repCount ?? null;
      (done[entry.nodeId] ||= []).push({ reps, corrected: entry.actualReps != null });
    }
  }
  return done;
};

/** Sets done per Work node id; null without a log. */
export const setsDoneById = (planData) => {
  const performed = performedSetsById(planData);
  if (!performed) return null;
  return Object.fromEntries(Object.entries(performed).map(([id, sets]) => [id, sets.length]));
};

const loadText = (lift) =>
  lift.weight
    ? ` @ ${join(lift.weight)} kg`
    : lift.intensityPct
      ? ` @ ${join(lift.intensityPct)}% 1RM`
      : "";

// Did any done set land off its prescribed reps?
const offTarget = (lift, sets) =>
  sets.some((set, i) => set.reps != null && lift.reps && set.reps !== lift.reps[Math.min(i, lift.reps.length - 1)]);

/**
 * "Bench Press: 4 x 5 @ 90/90/100/100 kg (4 of 4 sets done)", or with the
 * reps actually done when they differ from the target:
 * "Bench Press: 4 sets @ 90/90/100/100 kg, reps 8/6/8/8 (target 8)".
 * `done` is the performed sets (or a count of sets, or null for unknown).
 */
export const describeLift = (lift, done) => {
  const sets = Array.isArray(done) ? done : null;
  const count = sets ? sets.length : done;
  const ofSets = count == null ? "" : ` (${Math.min(count, lift.sets)} of ${lift.sets} sets done)`;
  if (sets && lift.reps && offTarget(lift, sets)) {
    const target = (i) => lift.reps[Math.min(i, lift.reps.length - 1)];
    const got = sets.map((set, i) => set.reps ?? target(i)).join("/");
    const short = count < lift.sets ? `, ${Math.min(count, lift.sets)} of ${lift.sets} sets done` : "";
    return `${lift.label}: ${lift.sets} sets${loadText(lift)}, reps ${got} (target ${join(lift.reps)}${short})`;
  }
  const reps = lift.reps ? join(lift.reps) : null;
  const shape = reps ? `${lift.sets} x ${reps}` : `${lift.sets} sets`;
  return `${lift.label}: ${shape}${loadText(lift)}${ofSets}`;
};

/**
 * How last week's same day went, for the generator. Null when the saved
 * session has no main lifts to report.
 *
 * @returns {{ lines: string[], allSetsDone: boolean|null, repsShort: boolean,
 *             repsKnown: boolean }}
 *   repsShort: some done set fell below its target reps.
 *   repsKnown: the user confirmed reps (#97), so "every set hit the target" is real.
 */
export const summarisePerformance = (planData) => {
  const lifts = extractLifts(planData).slice(0, MAX_LIFTS);
  if (!lifts.length) return null;
  const performed = performedSetsById(planData);
  const setsFor = (lift) => (performed ? performed[lift.id] || [] : null);
  const lines = lifts.map((lift) => describeLift(lift, setsFor(lift)));
  const allSetsDone = performed ? lifts.every((lift) => setsFor(lift).length >= lift.sets) : null;
  const repsShort = Boolean(performed) && lifts.some((lift) =>
    setsFor(lift).some((set, i) => lift.reps && set.reps != null && set.reps < lift.reps[Math.min(i, lift.reps.length - 1)]),
  );
  const repsKnown = Boolean(performed) && lifts.some((lift) => setsFor(lift).some((set) => set.corrected));
  return { lines, allSetsDone, repsShort, repsKnown };
};

/**
 * What to do with the load this week, from how the same day went last week.
 * `rating` is the session's effort (RPE 1-10), never enjoyment.
 *
 *   sets missed, or effort 9-10 -> hold the load and own every set first
 *   effort 7-8 (or unrated)     -> progress as the framework says
 *   effort 6 or lower           -> it was easy: progress a little more
 */
export const progressionAdvice = ({ rating, allSetsDone, repsShort = false, repsKnown = false } = {}) => {
  const effort = Number(rating);
  const rated = Number.isFinite(effort) && effort >= 1 && effort <= 10;
  if (allSetsDone === false) {
    return "Not every set was done last week, so HOLD the load: repeat last week's loads and aim to complete every set with good form before adding any.";
  }
  // Double progression on real reps (#97): reps first, then load.
  if (repsShort) {
    return "Some sets fell short of their target reps last week, so KEEP the load and aim to reach the target on every set before adding weight.";
  }
  if (rated && effort >= 9) {
    return `Last week's effort was ${effort}/10, close to the limit, so HOLD the load this week (add a rep only where it comes easily) and progress once it feels more manageable.`;
  }
  if (rated && effort <= 6) {
    return `Last week's effort was only ${effort}/10, so progress a little MORE than usual: up to two load steps or two extra reps on the main lifts, still stopping short of failure.`;
  }
  if (repsKnown) {
    return `Every set reached its target reps last week${rated ? ` at effort ${effort}/10` : ""}: add the smallest load step and go back to the bottom of the rep range.`;
  }
  return rated
    ? `Last week's effort was ${effort}/10: progress as the framework says, one small step from last week's loads.`
    : "Progress as the framework says, one small step from last week's loads.";
};
